// Inline Python code block runner — used inside AI assistant chat bubbles.
// Detects ```python fences in the message text and adds a ▶ Run button that
// executes the block via the shared Pyodide runtime. Runs entirely in the
// user's browser — the code and result never leave the tab.

import { useState } from 'react';
import { Play, CircleNotch, Warning, ChartLine, Code, ClipboardText } from '@phosphor-icons/react';
import { runPython, getPyodide } from '@/lib/pyodideRuntime';
import { toast } from 'sonner';

export function CodeBlockRunner({ code, lang }) {
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState('');
  const [output, setOutput] = useState(null);
  const [err, setErr] = useState(null);
  const [chart, setChart] = useState(null);

  const run = async () => {
    setLoading(true); setStage('起動中…'); setOutput(null); setErr(null); setChart(null);
    try {
      await getPyodide(setStage);
      setStage('');
      const r = await runPython(code);
      setOutput(r.output); setErr(r.error); setChart(r.chart);
      window.__pyodideReady = true;
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setLoading(false); setStage('');
    }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(code); toast.success('コードをコピーしました'); }
    catch { toast.error('コピーに失敗しました'); }
  };

  return (
    <div className="my-2 rounded-lg border border-zinc-300 overflow-hidden bg-zinc-950" data-testid="assistant-code-block">
      <div className="px-3 py-1.5 bg-zinc-900 border-b border-zinc-800 flex items-center gap-2">
        <Code size={12} className="text-cyan-400" />
        <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">{lang || 'python'}</span>
        <div className="flex-1" />
        <button type="button" onClick={copy}
          className="text-[11px] px-2 py-0.5 rounded text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 transition-colors flex items-center gap-1"
          data-testid="assistant-code-copy">
          <ClipboardText size={11} /> コピー
        </button>
        {lang === 'python' && (
          <button type="button" onClick={run} disabled={loading}
            className="text-[11px] px-2 py-0.5 rounded bg-cyan-600 hover:bg-cyan-500 disabled:opacity-60 text-white font-semibold flex items-center gap-1 transition-colors"
            data-testid="assistant-code-run">
            {loading ? <><CircleNotch size={11} className="animate-spin" />{stage || '実行中…'}</>
                     : <><Play size={11} weight="fill" />ブラウザで実行</>}
          </button>
        )}
      </div>
      <pre className="px-3 py-2 text-[12px] font-mono text-emerald-200 overflow-x-auto max-h-[280px]">{code}</pre>
      {(output || err || chart) && (
        <div className="bg-zinc-900 border-t border-zinc-800">
          {(output || err) && (
            <pre className={`px-3 py-2 text-[12px] font-mono whitespace-pre-wrap max-h-[220px] overflow-y-auto ${
              err ? 'text-rose-300' : 'text-zinc-200'}`} data-testid="assistant-code-output">
              {err ? (<>
                <Warning size={11} weight="fill" className="inline mr-1 text-rose-400" />
                {err}{output ? `\n\n${output}` : ''}
              </>) : output}
            </pre>
          )}
          {chart && (
            <div className="border-t border-zinc-800 bg-white p-2">
              <div className="flex items-center gap-1.5 mb-1.5 text-[10px] text-zinc-500">
                <ChartLine size={11} weight="fill" className="text-violet-500" />生成されたグラフ
              </div>
              <img src={chart} alt="chart" className="w-full rounded" data-testid="assistant-code-chart" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Split a message text into an array of {type:'text'|'code', content, lang} pieces
// so we can render text spans + interactive code blocks inline.
export function splitMessageByCodeFences(text) {
  if (!text) return [];
  const parts = [];
  const re = /```(\w+)?\n([\s\S]*?)```/g;
  let lastEnd = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastEnd) {
      parts.push({ type: 'text', content: text.slice(lastEnd, m.index) });
    }
    parts.push({ type: 'code', lang: (m[1] || '').toLowerCase() || 'text', content: m[2] });
    lastEnd = re.lastIndex;
  }
  if (lastEnd < text.length) {
    parts.push({ type: 'text', content: text.slice(lastEnd) });
  }
  return parts.length ? parts : [{ type: 'text', content: text }];
}
